'use client';

import { useEffect, useRef } from 'react';

type UseRevealOnIntersectOptions<T extends HTMLElement> = {
  selector?: string;
  threshold?: number;
};

export function useRevealOnIntersect<T extends HTMLElement>({
  selector = '.reveal, .reveal-scale',
  threshold = 0.12,
}: UseRevealOnIntersectOptions<T> = {}) {
  const sectionRef = useRef<T>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const elements = Array.from(section.querySelectorAll(selector));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold }
    );

    elements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [selector, threshold]);

  return sectionRef;
}
